import React, { useEffect, useState } from "react";
import { EsBadge } from "../components/EsBadge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Plus, HeartPulse, History, Trash2, Search, ChevronDown } from "lucide-react";
import { ParametriChart } from "../components/ParametriPanel";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { ExportButton } from "../components/ExportButton";
import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "../components/Segnalazioni";
import { MeseGiorniEsplosi, RecapOperatoriMese, AllertaBox } from "../components/MeseGiorniEsplosi";
import { FreqParametriInline } from "../components/FreqParametriInline";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { CorrettoBadge } from "../components/CorrettoBadge";
import { ElencoCorrezioniBtn, useCorrezioni } from "../components/ElencoCorrezioni";

// key, label, short, unit
const PARAMS = [
  { key: "pressione_sis", label: "Pressione max", short: "P.max", col: "Press. max", unit: "mmHg", normale: "90–139" },
  { key: "pressione_dia", label: "Pressione min", short: "P.min", col: "Press. min", unit: "mmHg", normale: "50–89" },
  { key: "fc", label: "Freq. cardiaca", short: "FC", col: "Freq. card.", unit: "bpm", normale: "50–100" },
  { key: "temp", label: "Temperatura", short: "Temp", col: "Temp.", unit: "°C", normale: "< 37,5" },
  { key: "spo2", label: "Saturazione O₂", short: "SpO₂", col: "Sat. O₂", unit: "%", normale: "≥ 95" },
  { key: "glicemia", label: "Glicemia", short: "Glic", col: "Glicemia", unit: "mg/dl", normale: "70–179" },
  { key: "fr", label: "Freq. respiratoria", short: "FR", col: "Freq. resp.", unit: "atti/min", normale: "10–20" },
  { key: "peso", label: "Peso", short: "Peso", col: "Peso", unit: "kg", normale: "—" },
  { key: "dolore", label: "Dolore", short: "Dolore", col: "Dolore", unit: "0-10", normale: "0–3" },
];

// return "danger" | "warn" | "" per value
const flag = (key, v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  switch (key) {
    case "temp": return n >= 38 ? "danger" : n >= 37.5 ? "warn" : "";
    case "spo2": return n < 92 ? "danger" : n < 95 ? "warn" : "";
    case "pressione_sis": return n >= 160 || n < 90 ? "danger" : n >= 140 ? "warn" : "";
    case "pressione_dia": return n >= 100 || n < 50 ? "danger" : n >= 90 ? "warn" : "";
    case "fc": return n > 110 || n < 45 ? "danger" : n > 100 || n < 50 ? "warn" : "";
    case "glicemia": return n >= 250 || n < 60 ? "danger" : n >= 180 || n < 70 ? "warn" : "";
    case "fr": return n > 24 || n < 8 ? "danger" : n > 20 || n < 10 ? "warn" : "";
    case "dolore": return n >= 7 ? "danger" : n >= 4 ? "warn" : "";
    default: return "";
  }
};
const flagClass = (f) => f === "danger" ? "bg-red-100 text-red-700 font-bold" : f === "warn" ? "bg-amber-100 text-amber-700 font-semibold" : "";
const fmtDateTime = (iso) => { if (!iso) return "—"; const d = new Date(iso); return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
const giorniDaMisura = (iso) => { if (!iso) return null; try { const t = new Date(); t.setHours(0, 0, 0, 0); const d = new Date(iso); d.setHours(0, 0, 0, 0); return Math.round((t - d) / 86400000); } catch { return null; } };
function GiorniUltimaBadge({ u, freq, testid }) {
  if (!u) return <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-red-100 text-red-700" data-testid={testid}>Mai misurato</span>;
  const g = giorniDaMisura(u.data);
  if (g === null) return null;
  const soglia = freq || 1;
  const tone = g === 0 ? "bg-emerald-100 text-emerald-700" : (g > soglia ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700");
  const txt = g === 0 ? "misurato oggi" : g === 1 ? "da 1 giorno" : `da ${g} giorni`;
  return <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${tone}`} data-testid={testid}>{txt}</span>;
}
function ProssimaMisuraBadge({ u, freq, testid }) {
  if (!freq || !u) return null;
  const g = giorniDaMisura(u.data);
  if (g === null) return null;
  const restano = freq - g;
  const tone = restano <= 0 ? "bg-red-100 text-red-700" : restano <= 1 ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700";
  const txt = restano <= 0 ? "da misurare" : restano === 1 ? "prossima tra 1 giorno" : `prossima tra ${restano} giorni`;
  return <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${tone}`} data-testid={testid}>{txt}</span>;
}
const fmtMisuraCell = (iso) => { if (!iso) return null; try { const d = new Date(iso); return { data: d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }), ora: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` }; } catch { return null; } };

export default function Parametri() {
  const { selectedStructure, structures } = useAuth();
  const { activeOperatore, opName, canSign, signerName, mustUnlock } = useFirma();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null); // { ospite, form }
  const [storico, setStorico] = useState(null); // { ospite }
  const [showDimessi, setShowDimessi] = useState(false);

  const { data: rows = [] } = useQuery({ queryKey: ["parametri", selectedStructure], queryFn: async () => (await api.get("/parametri/overview", { params: { structure_id: selectedStructure } })).data });
  const parToday = new Date().toISOString().slice(0, 10);
  const [openDay, setOpenDay] = useState(parToday);
  const [mese, setMese] = useState(parToday.slice(0, 7));
  const selDate = openDay || parToday;
  const { data: meseData } = useQuery({ queryKey: ["parametri-mese", selectedStructure, mese], queryFn: async () => (await api.get("/parametri/mese", { params: { structure_id: selectedStructure, mese } })).data });
  const { data: dayData } = useQuery({ queryKey: ["parametri-day", selectedStructure, selDate], queryFn: async () => (await api.get("/parametri/day", { params: { structure_id: selectedStructure, data: selDate } })).data });
  const dayRows = dayData?.rows || [];
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni("parametri", selectedStructure, selDate);
  const corrMap = useCorrezioni("parametri", selectedStructure, selDate.slice(0, 7));
  const { data: storicoRecs = [] } = useQuery({ queryKey: ["parametri-storico", storico?.ospite_id], enabled: !!storico, queryFn: async () => (await api.get(`/parametri/storico/${storico.ospite_id}`)).data });

  const structName = (id) => structures.find((s) => s.id === id)?.nome || "";
  const allStruct = selectedStructure === "all";
  const term = q.trim().toLowerCase();
  const filtered = rows.filter((r) => !term || `${r.nome} ${r.cognome}`.toLowerCase().includes(term));
  const dayFiltered = dayRows.filter((r) => !term || `${r.nome} ${r.cognome}`.toLowerCase().includes(term));
  const isDimesso = (r) => !!(r.data_dimissione && String(r.data_dimissione).trim());
  const dayAttivi = dayFiltered.filter((r) => !isDimesso(r));
  const dayDimessi = dayFiltered.filter((r) => isDimesso(r));

  const parColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#e11d48";
  const parName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const parCols = [
    { header: "Ospite", accessor: (r) => `${r.cognome} ${r.nome}` },
    { header: "Struttura", accessor: (r) => structName(r.structure_id) },
    ...PARAMS.map((p) => ({ header: `${p.label} (${p.unit})`, accessor: (r) => { const v = r.ultimo ? r.ultimo[p.key] : null; return v === null || v === undefined ? "" : v; } })),
    { header: "Ultima misura", accessor: (r) => (r.ultimo ? fmtDateTime(r.ultimo.data) : "mai") },
  ];

  const openDlg = (r) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per rilevare i parametri"); return; }
    setDlg({ ospite: r, form: { operatore: signerName } });
  };
  const save = async () => {
    const f = dlg.form;
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN)"); return; }
    const hasAny = PARAMS.some((p) => f[p.key] !== undefined && f[p.key] !== "");
    if (!hasAny) { toast.error("Inserisci almeno un parametro"); return; }
    try {
      const body = { ospite_id: dlg.ospite.ospite_id, structure_id: dlg.ospite.structure_id, operatore: signerName, note: f.note || "", data: `${selDate}T${new Date().toTimeString().slice(0, 8)}` };
      PARAMS.forEach((p) => { body[p.key] = f[p.key]; });
      await api.post("/parametri/register", body);
      qc.invalidateQueries({ queryKey: ["parametri"] });
      qc.invalidateQueries({ queryKey: ["parametri-day"] });
      qc.invalidateQueries({ queryKey: ["parametri-storico"] });
      qc.invalidateQueries({ queryKey: ["parametri-mese"] });
      setDlg(null);
      toast.success("Parametri registrati");
    } catch { toast.error("Errore nel salvataggio"); }
  };
  // ospiti con almeno un valore critico nell'ultima misurazione
  const fuoriSoglia = filtered.filter((r) => r.ultimo && PARAMS.some((p) => flag(p.key, r.ultimo[p.key]) === "danger"))
    .map((r) => ({ ...r, _crit: PARAMS.filter((p) => flag(p.key, r.ultimo[p.key]) === "danger").map((p) => `${p.label} ${r.ultimo[p.key]}`) }));
  const delRec = async (id) => { if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN)"); return; } if (!window.confirm("Eliminare questa misurazione? Resterà nell'Elenco correzioni.")) return; try { await api.delete(`/parametri/rec/${id}`, { params: { operatore: signerName } }); qc.invalidateQueries({ queryKey: ["parametri-storico"] }); qc.invalidateQueries({ queryKey: ["parametri"] }); qc.invalidateQueries({ queryKey: ["parametri-day"] }); qc.invalidateQueries({ queryKey: ["correzioni"] }); } catch { toast.error("Errore"); } };
  const renderCard = (r, num) => {
    const u = r.ultimo; const dim = isDimesso(r);
    return (
      <div key={r.ospite_id} className="rounded-xl border p-3 bg-white" data-testid={`par-card-${r.ospite_id}`}>
        <div className="flex flex-col gap-2 mb-2">
          <div className="min-w-0 flex items-center gap-2">
            <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span>
            <div className="min-w-0">
              <div className="font-semibold truncate flex items-center gap-2"><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id || r.id}?tab=parametri`); }} className="hover:underline text-left truncate" data-testid={`par-nome-${r.ospite_id || r.id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id || r.id} /><CorrettoBadge correzioni={corrMap[`${r.ospite_id}_${selDate}`]} testid={`par-corretto-${r.ospite_id}`} />{dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}<GiorniUltimaBadge u={u} freq={r.parametri_frequenza_giorni} testid={`par-giorni-m-${r.ospite_id}`} /><ProssimaMisuraBadge u={u} freq={r.parametri_frequenza_giorni} testid={`par-prossima-m-${r.ospite_id}`} /></div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">{u ? `Ultima: ${fmtDateTime(u.data)}` : "Nessuna misura"}<FreqParametriInline ospiteId={r.ospite_id} freq={r.parametri_frequenza_giorni} nome={`${r.cognome} ${r.nome}`} /></div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 w-full">
            <Button onClick={() => openDlg(r)} className="h-10 px-3 flex-1" data-testid={`par-add-m-${r.ospite_id}`}><Plus size={16} className="mr-1" /> Rileva</Button>
            <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setStorico(r)} data-testid={`par-storico-m-${r.ospite_id}`}><History size={16} /></Button>
            <SegnalazioneButton categoria="parametri" structureId={selectedStructure} data={selDate} ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`} entry={segByKey[`${r.ospite_id}_${selDate}`]} onChanged={segRefetch} />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PARAMS.map((p) => { const val = u ? u[p.key] : null; const f = flag(p.key, val); return (
            <span key={p.key} className={`text-[11px] rounded px-1.5 py-0.5 tabnum ${flagClass(f)}`} data-testid={`par-m-${r.ospite_id}-${p.key}`}>{p.label}: {val === null || val === undefined ? "—" : <>{val}{p.key !== "dolore" && <span className="text-[9px] text-muted-foreground font-normal ml-0.5">{p.unit}</span>}</>}</span>
          ); })}
        </div>
      </div>
    );
  };
  const renderRow = (r, num) => {
    const u = r.ultimo; const dim = isDimesso(r);
    return (
      <tr key={r.ospite_id} className="border-t" data-testid={`par-row-${r.ospite_id}`}>
        <td className="p-1.5 sticky left-0 bg-white font-semibold align-top">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-grid place-items-center h-5 w-5 rounded-full bg-secondary text-muted-foreground text-[10px] font-bold tabnum">{num != null ? num : "–"}</span>
            <span><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id || r.id}?tab=parametri`); }} className="hover:underline text-left">{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id || r.id} /><CorrettoBadge correzioni={corrMap[`${r.ospite_id}_${selDate}`]} testid={`par-corretto-${r.ospite_id}`} /></span>
            {dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Dimesso</span>}
            <GiorniUltimaBadge u={u} freq={r.parametri_frequenza_giorni} testid={`par-giorni-${r.ospite_id}`} />
            <ProssimaMisuraBadge u={u} freq={r.parametri_frequenza_giorni} testid={`par-prossima-${r.ospite_id}`} />
            <FreqParametriInline ospiteId={r.ospite_id} freq={r.parametri_frequenza_giorni} nome={`${r.cognome} ${r.nome}`} />
          </div>
          <div className="text-[11px] font-normal mt-0.5" data-testid={`par-misura-${r.ospite_id}`}>
            {(() => { const m = fmtMisuraCell(u?.data); return m ? <span className="text-muted-foreground">Ultima misura: <span className="capitalize text-foreground font-semibold">{m.data}</span> · Ore {m.ora}{u?.operatore ? ` · ✍️ ${u.operatore}` : ""}</span> : <span className="text-muted-foreground">Nessuna misura</span>; })()}
          </div>
          {allStruct && r.structure_id ? <span className="block text-[11px] text-muted-foreground font-normal">{structName(r.structure_id)}</span> : ""}
        </td>
        {PARAMS.map((p) => { const val = u ? u[p.key] : null; const f = flag(p.key, val); return <td key={p.key} className="px-0.5 py-1 text-center"><span className={`inline-block min-w-[22px] rounded px-0.5 py-0.5 tabnum ${flagClass(f)}`} data-testid={`par-${r.ospite_id}-${p.key}`}>{val === null || val === undefined ? "—" : <>{val}{p.key !== "dolore" && <span className="text-[9px] text-muted-foreground font-normal ml-0.5">{p.unit}</span>}</>}</span></td>; })}
        <td className="px-1 py-1.5 text-center whitespace-nowrap">
          <Button size="sm" onClick={() => openDlg(r)} data-testid={`par-add-${r.ospite_id}`}><Plus size={14} className="mr-1" /> Rileva</Button>
          <Button size="sm" variant="ghost" onClick={() => setStorico(r)} data-testid={`par-storico-${r.ospite_id}`}><History size={14} /></Button>
          <SegnalazioneButton categoria="parametri" structureId={selectedStructure} data={selDate} ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`} entry={segByKey[`${r.ospite_id}_${selDate}`]} onChanged={segRefetch} />
        </td>
      </tr>
    );
  };


  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><HeartPulse size={22} className="text-rose-600" /> Parametri vitali</h1>
          <p className="text-sm text-muted-foreground">Controlla e monitora i parametri di tutti gli ospiti. I valori fuori norma sono evidenziati.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSign
            ? <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3 py-1.5 text-sm font-semibold" data-testid="par-operatore-attivo">{signerName}</span>
            : <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-sm font-semibold" data-testid="par-operatore-mancante">Sblocca con la Firma Rapida</span>}
          <ExportButton filename="parametri" title="Parametri vitali" subtitle={parName} columns={parCols} rows={filtered} color={parColor} testid="par-export" />
          <SegnalazioniBadge categoria="parametri" aperte={segAperte} />
          <ElencoCorrezioniBtn categoria="parametri" structureId={selectedStructure} mese={mese} />
        </div>
      </div>

      <MeseGiorniEsplosi embedded color="#e11d48"
        mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
        counts={meseData?.counts || {}} countLabel="misurazioni"
        monthTop={<>
          <AllertaBox tone="bad" title="Parametri fuori soglia (valori critici)" icon={HeartPulse}
            items={fuoriSoglia} testid="par-allerta-box" onItemClick={(it) => navigate(`/ospiti/${it.ospite_id}?tab=parametri`)}
            renderItem={(it) => (<span><b>{it.cognome} {it.nome}</b> <span className="ml-1 text-red-700 font-bold">{it._crit.join(", ")}</span></span>)} />
          <RecapOperatoriMese operatori={meseData?.operatori || []} color="#e11d48" label="misurazioni" icon={HeartPulse} />
        </>}>

      <div className="relative mb-3 max-w-md" data-testid="par-search-wrap">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ospite (nome o cognome)" className="pl-8 w-full" data-testid="par-search" />
      </div>
      <div className="gr-card p-3 mb-4" data-testid="par-legenda">
        <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Valori normali di riferimento
          <span className="ml-2 inline-flex items-center gap-1 font-semibold normal-case"><span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300" /> attenzione</span>
          <span className="inline-flex items-center gap-1 font-semibold normal-case"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" /> critico</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {PARAMS.filter((p) => p.normale !== "—").map((p) => (
            <div key={p.key} className="rounded-lg border bg-emerald-50/60 px-2.5 py-1.5" data-testid={`par-legenda-${p.key}`}>
              <div className="text-[11px] text-muted-foreground font-semibold leading-tight">{p.label}</div>
              <div className="text-sm font-head font-bold text-emerald-800 tabnum">{p.normale} <span className="text-[10px] font-normal text-muted-foreground">{p.unit}</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2 md:hidden" data-testid="par-mobile-list">
        {dayFiltered.length === 0 && <p className="text-sm text-muted-foreground">Nessun ospite</p>}
        {dayAttivi.map((r, i) => renderCard(r, i + 1))}
        {dayDimessi.length > 0 && (
          <div data-testid="par-dimessi-section-m">
            <button onClick={() => setShowDimessi((v) => !v)} data-testid="par-toggle-dimessi-m" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 mt-2">
              <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
              <span className="font-bold text-sm text-orange-800">Dimessi</span>
              <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dayDimessi.length}</span>
            </button>
            {showDimessi && <div className="space-y-2 mt-2">{dayDimessi.map((r) => renderCard(r, null))}</div>}
          </div>
        )}
      </div>

      <div className="gr-card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="text-left px-1.5 py-2 sticky left-0 bg-secondary/60 min-w-[112px]">Ospite</th>
              {PARAMS.map((p) => <th key={p.key} className="px-0.5 py-2 text-center leading-tight" title={`${p.label} (${p.unit})`}>{p.col}</th>)}
              <th className="px-1 py-2 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {dayFiltered.length === 0 && <tr><td colSpan={99} className="text-center py-10 text-muted-foreground">Nessun ospite</td></tr>}
            {dayAttivi.map((r, i) => renderRow(r, i + 1))}
            {dayDimessi.length > 0 && (
              <>
                <tr data-testid="par-dimessi-row">
                  <td colSpan={99} className="p-0">
                    <button onClick={() => setShowDimessi((v) => !v)} data-testid="par-toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2 bg-orange-50 border-y border-orange-200">
                      <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                      <span className="font-bold text-sm text-orange-800">Dimessi</span>
                      <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dayDimessi.length}</span>
                    </button>
                  </td>
                </tr>
                {showDimessi && dayDimessi.map((r) => renderRow(r, null))}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground mt-3 flex flex-wrap gap-4">
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-100 inline-block" /> Da tenere sotto controllo</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-100 inline-block" /> Valore critico</span>
      </div>
      </MeseGiorniEsplosi>

      {/* Registrazione */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rileva parametri — {dlg?.ospite?.cognome} {dlg?.ospite?.nome}</DialogTitle>
            <DialogDescription>Inserisci i valori misurati. Lascia vuoti quelli non rilevati.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PARAMS.map((p) => (
              <div key={p.key}>
                <Label className="text-xs font-semibold mb-1 block">{p.label} <span className="text-muted-foreground font-normal">{p.unit}</span></Label>
                <Input type="number" step="any" value={dlg?.form?.[p.key] ?? ""} onChange={(e) => setDlg((d) => ({ ...d, form: { ...d.form, [p.key]: e.target.value } }))} data-testid={`par-input-${p.key}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Operatore *</Label>
              <OperatoreLocked name={signerName} testid="par-dlg-op" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Note</Label>
              <Input value={dlg?.form?.note || ""} onChange={(e) => setDlg((d) => ({ ...d, form: { ...d.form, note: e.target.value } }))} data-testid="par-input-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
            <Button onClick={save} data-testid="par-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storico */}
      <Dialog open={!!storico} onOpenChange={(o) => !o && setStorico(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Activity size={18} /> Storico parametri — {storico?.cognome} {storico?.nome}</DialogTitle>
            <DialogDescription>Andamento delle misurazioni nel tempo (più recenti in cima).</DialogDescription>
          </DialogHeader>
          <div className="mb-3"><ParametriChart data={storicoRecs} /></div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="text-left p-2 whitespace-nowrap">Data/ora</th>
                {PARAMS.map((p) => <th key={p.key} className="p-2 text-center whitespace-nowrap">{p.label}</th>)}
                <th className="p-2 text-left">Operatore</th><th className="p-2"></th>
              </tr></thead>
              <tbody>
                {storicoRecs.length === 0 && <tr><td colSpan={99} className="text-center py-8 text-muted-foreground">Nessuna misurazione registrata</td></tr>}
                {storicoRecs.map((rec) => (
                  <tr key={rec.id} className="border-t" data-testid={`par-storico-row-${rec.id}`}>
                    <td className="p-2 whitespace-nowrap font-semibold">{fmtDateTime(rec.data)}</td>
                    {PARAMS.map((p) => { const f = flag(p.key, rec[p.key]); return <td key={p.key} className="p-1 text-center"><span className={`inline-block min-w-[30px] rounded px-1 tabnum ${flagClass(f)}`}>{rec[p.key] === null || rec[p.key] === undefined ? "—" : <>{rec[p.key]}{p.key !== "dolore" && <span className="text-[9px] text-muted-foreground font-normal ml-0.5">{p.unit}</span>}</>}</span></td>; })}
                    <td className="p-2 text-xs">{rec.operatore || "—"}</td>
                    <td className="p-2 text-center"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => delRec(rec.id)} data-testid={`par-del-${rec.id}`}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
